import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

interface Kontakt {
  id: string;
  kundeId: string;
  objektId: string | null;
  vorname: string | null;
  nachname: string;
  rolle: string | null;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  istHauptkontakt: boolean;
  notizen: string | null;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/kontakte')({
  beforeLoad: () => requireAccess('/api/v1/kontakte'),
  component: KontaktePage,
});

function KontaktePage() {
  const qc = useQueryClient();
  const [kundeFilter, setKundeFilter] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const listQ = useQuery({
    queryKey: ['kontakte', kundeFilter],
    queryFn: () =>
      api<{ items: Kontakt[] }>(`/api/v1/kontakte${kundeFilter ? `?kundeId=${kundeFilter}` : ''}`),
  });
  const [showForm, setShowForm] = useState(false);

  const kundeMap = new Map((kundenQ.data?.items ?? []).map((k) => [k.id, k]));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Kontakte"
        description="Ansprechpartner pro Kunde (optional pro Objekt). Ein Hauptkontakt pro Kunde."
        actions={
          <Button onClick={() => setShowForm((s) => !s)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? 'Abbrechen' : '+ Neuer Kontakt'}
          </Button>
        }
      />

      <div className="flex items-center gap-3 text-sm">
        <label>
          <span className="text-slate-600 mr-2">Kunde</span>
          <Select
            value={kundeFilter}
            onChange={(e) => setKundeFilter(e.target.value)}
            className="w-72 inline-block"
          >
            <option value="">alle</option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer} — {k.firmenname}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {showForm && kundenQ.data && (
        <KontaktCreateForm
          kunden={kundenQ.data.items}
          defaultKundeId={kundeFilter || kundenQ.data.items[0]?.id || ''}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['kontakte'] });
          }}
        />
      )}

      <ErrorBanner error={listQ.error} />

      {listQ.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Kunde</th>
                <th className="px-4 py-2 text-left">Rolle</th>
                <th className="px-4 py-2 text-left">E-Mail / Tel.</th>
                <th className="px-4 py-2 text-center w-32">Hauptkontakt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listQ.data.items.map((k) => {
                const kd = kundeMap.get(k.kundeId);
                return (
                  <tr key={k.id} className="text-sm text-slate-800">
                    <td className="px-4 py-2 font-medium">
                      {[k.vorname, k.nachname].filter(Boolean).join(' ')}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {kd ? `${kd.kundennummer} — ${kd.firmenname}` : k.kundeId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{k.rolle ?? '–'}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {k.email && <div>{k.email}</div>}
                      {(k.telefon || k.mobil) && (
                        <div className="text-xs text-slate-500">
                          {[k.telefon, k.mobil].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {!k.email && !k.telefon && !k.mobil && '–'}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {k.istHauptkontakt && (
                        <span className="inline-flex items-center rounded-md border bg-amber-100 text-amber-800 border-amber-200 px-2 py-0.5 text-xs font-medium">
                          Haupt
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {listQ.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    Keine Kontakte.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KontaktCreateForm({
  kunden,
  defaultKundeId,
  onDone,
}: {
  kunden: Kunde[];
  defaultKundeId: string;
  onDone: () => void;
}) {
  const [st, setSt] = useState({
    kundeId: defaultKundeId,
    vorname: '',
    nachname: '',
    rolle: '',
    email: '',
    telefon: '',
    mobil: '',
    istHauptkontakt: false,
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        kundeId: st.kundeId,
        nachname: st.nachname,
        istHauptkontakt: st.istHauptkontakt,
      };
      if (st.vorname.trim()) body['vorname'] = st.vorname.trim();
      if (st.rolle.trim()) body['rolle'] = st.rolle.trim();
      if (st.email.trim()) body['email'] = st.email.trim();
      if (st.telefon.trim()) body['telefon'] = st.telefon.trim();
      if (st.mobil.trim()) body['mobil'] = st.mobil.trim();
      return api<{ item: Kontakt }>('/api/v1/kontakte', { method: 'POST', body });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">Kunde</label>
          <Select
            required
            value={st.kundeId}
            onChange={(e) => setSt({ ...st, kundeId: e.target.value })}
          >
            {kunden.map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer} — {k.firmenname}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Vorname</label>
          <Input value={st.vorname} onChange={(e) => setSt({ ...st, vorname: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Nachname</label>
          <Input
            required
            value={st.nachname}
            onChange={(e) => setSt({ ...st, nachname: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Rolle</label>
          <Input
            value={st.rolle}
            onChange={(e) => setSt({ ...st, rolle: e.target.value })}
            placeholder="Hausverwalter"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">E-Mail</label>
          <Input
            type="email"
            value={st.email}
            onChange={(e) => setSt({ ...st, email: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Telefon</label>
          <Input value={st.telefon} onChange={(e) => setSt({ ...st, telefon: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Mobil</label>
          <Input value={st.mobil} onChange={(e) => setSt({ ...st, mobil: e.target.value })} />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={st.istHauptkontakt}
              onChange={(e) => setSt({ ...st, istHauptkontakt: e.target.checked })}
            />
            Hauptkontakt
          </label>
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Kontakt anlegen'}
        </Button>
      </div>
    </form>
  );
}
